import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Manage", href: "/admin/retention" },
  { title: "Task Notifications", href: "/admin/cron" },
]

interface Recipient {
  id: number
  name: string
  email: string
}

interface NotificationHistoryEntry {
  label: string
  date: string
}

interface TaskNotification {
  id: number
  type: string
  task_type: string | null
  due_date: string
  case_id: number
  case_name: string | null
  customer_id: number
  customer_name: string | null
  contact_id: number
  contact_name: string | null
  event_id: number
  event_name: string | null
  request: string | null
  assigned_to: string | null
  urgent: boolean | number
  sensitive: boolean | number
  escalation_level: string
  to_recipients: Recipient[]
  cc_recipients: Recipient[]
  subject: string
  body: string
  link: string | null
  notification_history: NotificationHistoryEntry[]
}

interface EscalationStep {
  level: string
  label: string
  timing: string
  subject_prefix: string
  admin_cc: boolean
}

interface Props {
  notifications: TaskNotification[]
  escalation_schedule: EscalationStep[]
  notify_date: string
}

interface SendResult {
  sent_count: number
  errors: { id: number; type: string; error: string }[]
  details: {
    id: number
    type: string
    subject: string
    to: string[]
    cc: string[]
  }[]
}

function escalationBadgeColor(level: string): string {
  switch (level) {
    case "before":
      return "bg-blue-100 text-blue-800"
    case "on":
      return "bg-yellow-100 text-yellow-800"
    case "reminder1":
      return "bg-orange-100 text-orange-800"
    case "reminder2":
      return "bg-red-100 text-red-800"
    case "reminder3":
      return "bg-red-200 text-red-900"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

function escalationLabel(level: string): string {
  switch (level) {
    case "before":
      return "Upcoming"
    case "on":
      return "Due Today"
    case "reminder1":
      return "Overdue (24h)"
    case "reminder2":
      return "Overdue (3d)"
    case "reminder3":
      return "Overdue (7d)"
    default:
      return level
  }
}

function csrfToken(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? ""
  )
}

async function sendNotifications(
  notificationIds?: number[],
): Promise<SendResult> {
  const body: Record<string, unknown> = {}
  if (notificationIds) {
    body.notification_ids = notificationIds
  }

  const res = await fetch("/admin/cron/send", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    throw new Error(`Send failed: ${res.status}`)
  }

  return res.json() as Promise<SendResult>
}

export default function Index({
  notifications,
  escalation_schedule,
  notify_date,
}: Props) {
  const [sending, setSending] = useState(false)
  const [sendingId, setSendingId] = useState<number | null>(null)
  const [lastResult, setLastResult] = useState<SendResult | null>(null)

  const handleSendBatch = async () => {
    if (!confirm("Send the next batch of up to 3 notifications?")) return
    setSending(true)
    setLastResult(null)
    try {
      const result = await sendNotifications()
      setLastResult(result)
      router.reload({ only: ["notifications"] })
    } catch {
      setLastResult({
        sent_count: 0,
        errors: [{ id: 0, type: "", error: "Request failed" }],
        details: [],
      })
    } finally {
      setSending(false)
    }
  }

  const handleSendOne = async (id: number) => {
    if (!confirm("Send this notification now?")) return
    setSendingId(id)
    setLastResult(null)
    try {
      const result = await sendNotifications([id])
      setLastResult(result)
      router.reload({ only: ["notifications"] })
    } catch {
      setLastResult({
        sent_count: 0,
        errors: [{ id, type: "", error: "Request failed" }],
        details: [],
      })
    } finally {
      setSendingId(null)
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Task Notifications" />

      <div className="p-6">
        <div className="border-hpi-light-gray mb-4 flex items-center justify-between border-b pb-2">
          <h1 className="text-2xl font-semibold">Pending Task Notifications</h1>
          {notifications.length > 0 && (
            <button
              type="button"
              onClick={() => void handleSendBatch()}
              disabled={sending}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {sending ? "Sending..." : "Send Batch (up to 3)"}
            </button>
          )}
        </div>
        <p className="mb-6 text-sm text-gray-500">
          Send daily @ 7am ET &mdash; Showing notifications as of {notify_date}
        </p>

        {lastResult && (
          <div
            className={`mb-4 rounded p-3 text-sm ${
              lastResult.errors.length > 0
                ? "bg-yellow-50 text-yellow-800"
                : "bg-green-50 text-green-800"
            }`}
          >
            <strong>{lastResult.sent_count}</strong> notification(s) sent.
            {lastResult.errors.length > 0 && (
              <span className="ml-2 text-red-600">
                {lastResult.errors.length} error(s):{" "}
                {lastResult.errors.map((e) => e.error).join(", ")}
              </span>
            )}
          </div>
        )}

        {notifications.length > 0 ? (
          <ul className="space-y-4">
            {notifications.map((n) => (
              <li
                key={`${n.type}-${n.id}`}
                className="border-hpi-light-gray rounded-lg border bg-white shadow-sm"
              >
                <div className="border-hpi-light-gray flex items-start justify-between border-b px-4 py-3">
                  <div>
                    {n.case_id > 0 && n.case_name && (
                      <div className="text-sm font-medium text-gray-700">
                        #{n.case_id} &ndash; {n.case_name}
                      </div>
                    )}
                    {!n.case_id && n.customer_id > 0 && n.customer_name && (
                      <div className="text-sm font-medium text-gray-700">
                        Cust #{n.customer_id}: {n.customer_name}
                      </div>
                    )}
                    {!n.case_id && n.contact_id > 0 && n.contact_name && (
                      <div className="text-sm font-medium text-gray-700">
                        Contact #{n.contact_id}: {n.contact_name}
                      </div>
                    )}
                    {n.event_id > 0 && n.event_name && (
                      <div className="text-sm font-medium text-gray-700">
                        Event #{n.event_id}: {n.event_name}
                      </div>
                    )}
                    <div className="mt-1 text-sm text-gray-600">
                      {n.due_date} {n.task_type && <>{n.task_type}: </>}
                      {n.request}
                      {n.assigned_to && (
                        <span className="text-gray-500">
                          {" "}
                          for {n.assigned_to}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleSendOne(n.id)}
                      disabled={sendingId === n.id || sending}
                      className="rounded border border-blue-300 bg-white px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                    >
                      {sendingId === n.id ? "Sending..." : "Send"}
                    </button>
                    {n.urgent ? (
                      <span className="rounded bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                        URGENT
                      </span>
                    ) : null}
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold ${escalationBadgeColor(n.escalation_level)}`}
                    >
                      {escalationLabel(n.escalation_level)}
                    </span>
                  </div>
                </div>

                <div className="px-4 py-3">
                  {n.to_recipients.length > 0 && (
                    <div className="mb-1 text-sm">
                      <span className="font-semibold">To:</span>{" "}
                      {n.to_recipients.map((r) => r.email).join(", ")}
                    </div>
                  )}
                  {n.cc_recipients.length > 0 && (
                    <div className="text-hpi-red mb-1 text-sm">
                      <span className="font-semibold">CC:</span>{" "}
                      {n.cc_recipients.map((r) => r.email).join(", ")}
                    </div>
                  )}

                  <div className="mt-2 mb-2 text-sm">
                    <span className="font-semibold">Subject:</span> {n.subject}
                  </div>

                  <hr className="my-2 border-gray-200" />

                  <div
                    className="prose prose-sm max-w-none text-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: n.body }}
                  />

                  {n.link && (
                    <div className="mt-2 text-sm">
                      Link to Task:{" "}
                      <a
                        href={n.link}
                        className="text-blue-600 hover:underline"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {n.link}
                      </a>
                    </div>
                  )}

                  {n.notification_history.length > 0 && (
                    <div className="mt-3 rounded bg-gray-100 p-3 text-sm">
                      {n.notification_history.map((h) => (
                        <div key={h.label}>
                          {h.label}: {h.date}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded bg-green-50 p-4 text-green-700">
            No task notifications found
          </div>
        )}

        <div className="mt-8">
          <h2 className="mb-3 text-lg font-semibold">Escalation Schedule</h2>
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Level
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Timing
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Subject Prefix
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Admin CC
                </th>
              </tr>
            </thead>
            <tbody>
              {escalation_schedule.map((step) => (
                <tr key={step.level} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-semibold ${escalationBadgeColor(step.level)}`}
                    >
                      {step.label}
                    </span>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {step.timing}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {step.subject_prefix}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {step.admin_cc ? (
                      <span className="text-hpi-red font-semibold">Yes</span>
                    ) : (
                      <span className="text-gray-400">No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppLayout>
  )
}
